/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import { useState } from 'react';
import { ComponentAttribute } from '../../types/attributes';
import { ATTR_NAMES } from '../../types/attrNames';
import { CmsProduct } from '../../types/cmsProduct';
import { VerticalType } from '../../types/cmsTypes';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import {
  sendProductAnalytics,
  UserActionEnum,
  ComponentTypeEnum,
} from '../../utils/analytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { parseUrlAlias } from '../../utils/helpers/urlAlias';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { CmsImage } from '../CmsImage';
import { GoogleRating } from '../GoogleRating';
import { getGoogleRating, getGoogleRatingContext } from '../GoogleReview/utils';
import ContentCopy from '../icons/ContentCopy';
import { Rating } from '../Rating';
import { TRANSLATIONS } from './i18n';

type ProductCardHeaderProps = {
  product: CmsProduct;
  index: string;
  view_rating?: boolean;
  componentAttributes: ComponentAttribute[];
  vertical: VerticalType;
  url?: string;
  pushDataLayer: PushDataLayerFn;
  /**
   * Played down because the reader's details fall short of this lender's
   * criteria (CMS-1905 UC 4). Only the identity is muted — the card's Apply
   * button sits outside this component and stays at full prominence.
   */
  deEmphasised?: boolean;
};

const imageSizesMap: Partial<Record<VerticalType, string>> = {
  PERSONAL_LOANS: classNames('h-14 w-24 md:h-[65px] md:w-32'),
  BANKING: classNames('h-14 w-24 md:h-[65px] md:w-32'),
  INVESTMENTS: classNames('h-14 w-24 md:h-[65px] md:w-32'),
  TRAVEL_INSURANCE: classNames('h-14 w-24 md:h-[65px] md:w-32'),
  CAR_INSURANCE: classNames('h-14 w-24 md:h-[65px] md:w-32'),
};

const defaultSize = classNames('h-18 w-28 md:h-[97px] md:w-38');

const containerPadding: Partial<Record<VerticalType, string>> = {
  PERSONAL_LOANS: classNames('p-2 md:px-3 md:py-4'),
  BANKING: classNames('p-2 md:px-3 md:py-4'),
  INVESTMENTS: classNames('p-2 md:px-3 md:py-4'),
  TRAVEL_INSURANCE: classNames('p-spacing/gap/y-sm'),
  CAR_INSURANCE: classNames('px-spacing/gap/x-sm py-spacing/gap/y-sm'),
};

export const ProductCardHeader: React.FC<ProductCardHeaderProps> = (
  props: ProductCardHeaderProps,
) => {
  const {
    product,
    vertical,
    url,
    index,
    view_rating,
    componentAttributes,
    pushDataLayer,
    deEmphasised,
  } = props;
  const {
    product_name,
    product_image,
    url_alias,
    rate_score,
    rate_additional_information,
    attributes: productAttributes,
  } = product;
  const context = useCmsClientContext();
  // The stored alias may carry the `#usePDP` modal marker — it is a CMS flag,
  // not part of the canonical PDP URL, so strip it before linking (CMS-1924).
  const productHref = parseUrlAlias(url_alias).url || undefined;
  const staticText = getClientTranslations(TRANSLATIONS, context);
  const [showCopiedTooltip, setShowCopiedTooltip] = useState(false);

  if (!vertical) {
    return;
  }

  const pushProductAnalytic = (typeItem: 'Product Image' | 'Product Name') => {
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: `Product Card/${typeItem}`,
      componentType: ComponentTypeEnum.PRODUCT_CARD,
      event: 'product_interaction',
      index: index,
      url: url,
      product: product,
      pushDataLayer,
    });
  };

  const handleCopyPromoCode = async () => {
    if (promoCodeAttribute) {
      promoCodeAttributeValue;

      if (promoCodeAttributeValue) {
        try {
          await navigator.clipboard.writeText(promoCodeAttributeValue);
          setShowCopiedTooltip(true);
          setTimeout(() => {
            setShowCopiedTooltip(false);
          }, 1500);
        } catch (err) {
          console.error('Failed to copy promo code:', err);
        }
      }
    }
  };

  const imageRender = () => {
    const size = imageSizesMap[vertical] ?? defaultSize;
    const padding = containerPadding[vertical!];

    const commonProps = {
      alt: product_name ?? 'Product image',
      src: product_image ?? '',
      width: 112,
      height: 72,
      className: 'object-contain',
    };

    const img = (
      <CmsImage
        {...commonProps}
        sizes={{ _: 152 }}
        className={classNames('object-contain', size)}
      />
    );

    if (!vertical || vertical === 'CREDIT_CARD')
      return (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={productHref}
          onClick={() => pushProductAnalytic('Product Image')}
        >
          {img}
        </a>
      );

    return (
      <div className={classNames('h-18 w-28 md:h-[97px] md:w-38', padding)}>
        {img}
      </div>
    );
  };

  const promoCodeComponentAttr = componentAttributes.find((ca) => {
    const productAttr = productAttributes?.find(
      (pa) => pa?.name?.uid === ca.attribute?.uid,
    );
    return productAttr?.name?.name === ATTR_NAMES.PROMO_CODE;
  });

  const promoCodeAttribute = promoCodeComponentAttr
    ? {
        // ProductMetaConfig label overrides the display label; empty falls
        // back to the system default translation (CMS-1756). `label` is typed
        // non-nullable here but the GraphQL field is nullable — the mapper's
        // `?? ''` is the only bridge, so guard the call.
        label: promoCodeComponentAttr.label?.trim() || staticText.promo_code,
        additionalInfo: promoCodeComponentAttr.additional_information,
        attribute: productAttributes?.find(
          (pa) => pa?.name?.uid === promoCodeComponentAttr.attribute?.uid,
        ),
      }
    : undefined;

  const promoCodeAttributeValue =
    promoCodeAttribute?.attribute?.__typename ===
      'ComponentAttributesAttrPlainText' && promoCodeAttribute.attribute.value;

  const googleRating = getGoogleRating(product);

  return (
    <div
      className={classNames('flex items-center justify-start gap-4 md:gap-6', {
        'opacity-60': deEmphasised,
      })}
    >
      {/* Greyscale on top of the group's dimming, as the Change 2 prototype
          does: a bank logo is the one thing on the card that stays vivid at
          60% opacity, so it keeps drawing the eye to a loan the reader falls
          short of. */}
      <div
        className={classNames(
          'rounded-radius/tooltip border border-borders/default',
          { grayscale: deEmphasised },
        )}
      >
        {imageRender()}
      </div>
      <div className="flex min-w-0 flex-col items-start justify-start gap-spacing/padding/soft-6px">
        <h3 className="mhg-alpha-typography-title-2 md:mhg-alpha-title-1 break-words">
          {!vertical || vertical === 'CREDIT_CARD' ? (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={productHref ?? '#'}
              title={product_name ?? ''}
              onClick={() => pushProductAnalytic('Product Name')}
              className="hover:underline"
            >
              {product_name}
            </a>
          ) : (
            <span>{product_name}</span>
          )}
        </h3>
        {googleRating && (
          <GoogleRating
            rateScore={googleRating.rateScore}
            rateCount={googleRating.rateCount}
            productId={product.uid}
            onClick={() =>
              sendProductAnalytics({
                action: UserActionEnum.CLICK,
                componentTitle: 'Product Card/Review Module_See Review',
                componentType: ComponentTypeEnum.PRODUCT_CARD,
                event: 'product_interaction',
                index,
                url,
                product,
                pushDataLayer,
                ratingContext: getGoogleRatingContext(googleRating),
              })
            }
          />
        )}
        {rate_score && view_rating && (
          <div className="origin-left">
            <Rating
              className="text-nowrap py-spacing/padding/x-xs"
              score={rate_score}
              info={rate_additional_information ?? ''}
              mode="horizontal"
              starView={false}
            />
          </div>
        )}
        {promoCodeAttribute && (
          <div className="flex origin-left flex-wrap items-center gap-spacing/gap/soft-4px">
            <p className="mhg-alpha-typography-body-2 md:mhg-alpha-typography-body-1 text-text/secondary-lighter">
              {promoCodeAttribute.label}:
            </p>
            <p className="mhg-alpha-typography-bullet-2 md:mhg-alpha-typography-bullet-1 break-all text-fill/primary">
              {promoCodeAttributeValue}
            </p>
            <button
              type="button"
              onClick={handleCopyPromoCode}
              aria-label="Copy promo code"
              className="cursor-pointer border-none bg-transparent p-0"
            >
              <ContentCopy />
              {showCopiedTooltip && (
                <div className="mhg-alpha-typography-caption absolute z-10 rounded p-2 shadow-lg bg-white text-text/secondary-lighter">
                  {staticText.copied}
                </div>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
